import mixpanel from 'mixpanel-browser';
import { UAParser } from 'ua-parser-js';
import {
  ANALYTICS_CONSTANTS,
  APP_NAME,
  DEVICE_ID_STORAGE_KEY,
  ProviderUserInfo,
  AnalyticsProperties,
  DatePickerSelectionProps,
  NoteCopyProps,
  NoteSentenceProps,
  DispatchedEvent,
} from '@ax/constants';

export class AnalyticsService implements AnalyticsProperties {
  private readonly token: string;
  private readonly deviceId: string;
  private readonly userInfo: ProviderUserInfo;
  private readonly uaParser: UAParser;
  private readonly isMixpanelInitialized: boolean = false;

  constructor(token: string, userInfo: ProviderUserInfo, deviceId?: string) {
    this.uaParser = new UAParser();
    this.token = token;
    this.deviceId = deviceId ?? localStorage.getItem(DEVICE_ID_STORAGE_KEY) ?? '';
    this.userInfo = userInfo;

    if (this.token) {
      mixpanel.init(this.token, {
        debug: process.env.NODE_ENV === 'development',
        cross_subdomain_cookie: false,
      });

      this.isMixpanelInitialized = true;
    }
  }

  processProperties(properties: Record<string, any>) {
    const browser = this.uaParser.getBrowser();
    const os = this.uaParser.getOS();

    const props = {
      [ANALYTICS_CONSTANTS.properties.visitDateTime]: properties.visitDateTime,
      [ANALYTICS_CONSTANTS.properties.eventDateTime]: properties.eventDateTime,
      [ANALYTICS_CONSTANTS.properties.noteId]: properties.noteId,
      [ANALYTICS_CONSTANTS.properties.providerGuid]: properties.providerGuid ?? this.userInfo?.guid,
      [ANALYTICS_CONSTANTS.properties.providerUid]: properties.providerUid ?? this.userInfo?.uid,
      [ANALYTICS_CONSTANTS.properties.providerName]: properties.providerName ?? this.userInfo?.name,
      [ANALYTICS_CONSTANTS.properties.providerEmail]:
        properties.providerEmail ?? this.userInfo?.email,
      [ANALYTICS_CONSTANTS.properties.ipAddress]: properties.ipAddress,
      [ANALYTICS_CONSTANTS.properties.browserName]: properties.browserName ?? browser.name,
      [ANALYTICS_CONSTANTS.properties.browserVersion]: properties.browserVersion ?? browser.version,
      [ANALYTICS_CONSTANTS.properties.os]: properties.os ?? os.name,
      [ANALYTICS_CONSTANTS.properties.osVersion]: properties.osVersion ?? os.version,
      [ANALYTICS_CONSTANTS.properties.action]: properties.action,
      [ANALYTICS_CONSTANTS.properties.appName]: APP_NAME,
      [ANALYTICS_CONSTANTS.properties.redirectUrl]: properties.redirectUrl ?? '',
      [ANALYTICS_CONSTANTS.properties.method]: properties.method ?? '',
      [ANALYTICS_CONSTANTS.properties.enterprise]: properties.enterprise ?? this.userInfo?.siteName,
      [ANALYTICS_CONSTANTS.properties.ehr]: properties.ehr ?? this.userInfo?.ehrName,
      [ANALYTICS_CONSTANTS.properties.specialty]:
        properties.specialty ?? this.userInfo?.specialtyName,
      [ANALYTICS_CONSTANTS.properties.templateName]: properties.templateName,
      [ANALYTICS_CONSTANTS.properties.userConfirmation]: properties.userConfirmation,
      [ANALYTICS_CONSTANTS.properties.specialty]:
        properties.specialty ?? this.userInfo?.specialtyName,
      [ANALYTICS_CONSTANTS.properties.deviceId]: this.deviceId ?? '',
      [ANALYTICS_CONSTANTS.properties.acknowledged]: properties?.acknowledged,
      [ANALYTICS_CONSTANTS.properties.reason]: properties?.reason,
      [ANALYTICS_CONSTANTS.properties.textLength]: properties?.textLength,
    };

    return Object.fromEntries(Object.entries(props).filter(([_, v]) => v));
  }

  trackEvent(event: string, properties: Record<string, any> = {}): DispatchedEvent {
    if (this.isMixpanelInitialized) {
      mixpanel.track(event, properties);
    }

    return { event, properties };
  }

  onVisitAddition(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.visitAdd, props);
  }

  onVisitDeletion(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.visitDelete, props);
  }

  onVisitSelection(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.visitSelect, props);
  }

  onDatePickerSelection(properties: DatePickerSelectionProps) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.dateSelected]: properties.selectedDate,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.datePickerSelect, props);
  }

  onRecordingStart(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.recordingStart, props);
  }

  onRecordingStop(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.recordingStop, props);
  }

  onNoteCopied(properties: NoteCopyProps) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.noteSection]: properties.noteSection,
      [ANALYTICS_CONSTANTS.properties.copyMethod]: properties.method,
      [ANALYTICS_CONSTANTS.properties.copiedWordsCount]: properties.wordsCount,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteCopy, props);
  }

  onNoteEdit(properties: NoteSentenceProps) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.noteSection]: properties.noteSection,
      [ANALYTICS_CONSTANTS.properties.wordsAddedCount]: properties.wordsAdded,
      [ANALYTICS_CONSTANTS.properties.wordsRemovedCount]: properties.wordsRemoved,
      [ANALYTICS_CONSTANTS.properties.eventDateTime]: properties.eventDateTime,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteEdit, props);
  }

  onUserLogout(properties?: AnalyticsProperties) {
    const props = this.processProperties(properties ?? {});
    return this.trackEvent(ANALYTICS_CONSTANTS.event.logOut, props);
  }

  onVisitArchived(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.visitArchived, props);
  }

  onNoteUploaded(properties: AnalyticsProperties) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.uploadMethod]: properties.uploadMethod,
      [ANALYTICS_CONSTANTS.properties.packageAssigned]: properties.packageAssigned,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteUploaded, props);
  }

  onNoteUploadFailed(properties: AnalyticsProperties) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.uploadMethod]: properties.uploadMethod,
      [ANALYTICS_CONSTANTS.properties.packageAssigned]: properties.packageAssigned,
      [ANALYTICS_CONSTANTS.properties.errorMessage]: properties.errorMessage,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteUploadFailed, props);
  }

  onUserLogin(properties?: AnalyticsProperties) {
    const props = this.processProperties(properties ?? {});
    return this.trackEvent(ANALYTICS_CONSTANTS.event.logIn, props);
  }

  onUserLoginFailedPWD(properties?: AnalyticsProperties) {
    const props = this.processProperties(properties ?? {});
    return this.trackEvent(ANALYTICS_CONSTANTS.event.incorrectPassword, props);
  }

  onUsernameInput(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.loginUserName, props);
  }

  onSSORedirect(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.loginSSORedirect, props);
  }

  onSSOReturn(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.loginSSOReturn, props);
  }

  onCreatePin(properties: AnalyticsProperties) {
    const props = this.processProperties({
      ...properties,
      method: ANALYTICS_CONSTANTS.loginMethods.passcode,
    });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.createPin, props);
  }

  onVerifyPin(properties: AnalyticsProperties) {
    const props = this.processProperties({
      ...properties,
      method: ANALYTICS_CONSTANTS.loginMethods.passcode,
    });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.verifyPin, props);
  }

  onTapNotYou(properties: AnalyticsProperties) {
    const props = this.processProperties({
      ...properties,
      method: ANALYTICS_CONSTANTS.loginMethods.passcode,
    });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.tappedNotYou, props);
  }

  onEnteredValidPin(properties: AnalyticsProperties) {
    const props = this.processProperties({
      ...properties,
      method: ANALYTICS_CONSTANTS.loginMethods.passcode,
    });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.enteredValidPin, props);
  }

  onEnteredInvalidPin(properties: AnalyticsProperties) {
    const props = this.processProperties({
      ...properties,
      method: ANALYTICS_CONSTANTS.loginMethods.passcode,
    });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.enteredInvalidPin, props);
  }

  onAddNewTemplate(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.addNewTemplate, props);
  }

  onSaveTemplate(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.saveTemplate, props);
  }

  onUpdateTemplate(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.updateTemplate, props);
  }

  onDeleteTemplate(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.deleteTemplate, props);
  }

  onDeleteTemplateConfirm(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.deleteTemplateConfirm, props);
  }

  onClearTemplate(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.clearTemplate, props);
  }

  onClearTemplateConfirm(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.clearTemplateConfirm, props);
  }

  onTapResetPIN(properties?: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.tappedResetPIN, props);
  }

  onResetPIN(properties?: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.resetPIN, props);
  }

  onPreferencesViewed(properties?: AnalyticsProperties) {
    const props = this.processProperties(properties ?? {});
    return this.trackEvent(ANALYTICS_CONSTANTS.event.preferencesViewed, props);
  }

  onPreferencesSaved(properties: AnalyticsProperties) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.noteSection]: properties.noteSection,
      [ANALYTICS_CONSTANTS.properties.action]: properties.action,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.preferencesSaved, props);
  }

  onInfoModalOpened(properties: AnalyticsProperties) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.noteSection]: properties.noteSection,
      [ANALYTICS_CONSTANTS.properties.preferenceType]: properties.preferenceType,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.infoModalOpened, props);
  }

  onInfoModalClosed(properties: AnalyticsProperties) {
    const props = {
      ...this.processProperties(properties),
      [ANALYTICS_CONSTANTS.properties.noteSection]: properties.noteSection,
      [ANALYTICS_CONSTANTS.properties.preferenceType]: properties.preferenceType,
    };
    return this.trackEvent(ANALYTICS_CONSTANTS.event.infoModalClosed, props);
  }

  onCareCuesAddToNoteButtonTapped(properties: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.careCuesAddToNoteButtonTapped, props);
  }

  onCareCuesArchived(properties: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.careCuesArchived, props);
  }

  onCareCuesSeen(properties: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.careCuesSeen, props);
  }

  onNavigatedToCareCuesFromCareCuesUploadModal(properties: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(
      ANALYTICS_CONSTANTS.event.navigatedToCareCuesFromCareCuesUploadModal,
      props,
    );
  }

  onUploadedNoteFromCareCuesUploadModal(properties: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.uploadedNoteFromCareCuesUploadModal, props);
  }

  onUploadCareCuesModalTriggered(properties: AnalyticsProperties) {
    const props = this.processProperties({ ...properties });
    return this.trackEvent(ANALYTICS_CONSTANTS.event.uploadCareCuesModalTriggered, props);
  }

  onVerbalizationEducationModal(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.verbalizationEducationModal, props);
  }

  onNoteLevelThumbsUp(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelThumbsUp, props);
  }

  onNoteLevelThumbsDown(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelThumbsDown, props);
  }

  onNoteLevelFeedbackReason(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelFeedbackReason, props);
  }

  onNoteLevelFeedbackTextSubmitted(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelFeedbackTextSubmitted, props);
  }

  onNoteLevelFeedbackModalOpened(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelFeedbackModalOpened, props);
  }

  onNoteLevelFeedbackModalClosed(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelFeedbackModalClosed, props);
  }

  onNoteLevelFeedbackReasonAdded(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelFeedbackReasonAdded, props);
  }

  onNoteLevelFeedbackReasonRemoved(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.noteLevelFeedbackReasonRemoved, props);
  }

  onLabInterpretationsViewed(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.labInterpretationsViewed, props);
  }

  onInterpretationRejected(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.interpretationRejected, props);
  }

  onAddReEvaluationModalOpened(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.addReEvaluationModalOpened, props);
  }

  onAddReEvaluationModalClosed(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.addReEvaluationModalClosed, props);
  }

  onInterpretationAccepted(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.interpretationAccepted, props);
  }

  onInterpretationEdited(properties: AnalyticsProperties) {
    const props = this.processProperties(properties);
    return this.trackEvent(ANALYTICS_CONSTANTS.event.interpretationEdited, props);
  }
}
